import React, { ChangeEventHandler, useState } from "react";
import { useForm } from "react-hook-form";

import Image from "../Image";
import Title from "../Typography/Title";
import Button from "../Button";
import informativaPolicy from "../../assets/informativa_privacy_candidati.pdf";

import formImage from "../../assets/vacancy-form-image.svg";

import * as S from "./style";

import { REACT_APP_MAIL_KEY, BREVO_ENDPOINT } from "../../constants";

type FormData = {
  name: string;
  surname: string;
  email: string;
  message: string;
  cv: FileList;
};

const VacancyForm: React.FC<{ title: string }> = ({ title }) => {
  const { register, handleSubmit, errors, reset } = useForm<FormData>();
  const [selectedFile, setSelectedFile] = useState<File>();
  const [policy, setPolicy] = useState(false);
  const [mailHasError, setMailHasError] = useState<boolean>(false);
  const [mailSended, setMailSended] = useState<boolean>(false);

  const convertBase64 = (file: Blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);

      reader.onload = () => {
        if (reader.result) {
          resolve((reader.result as string).split(",").pop());
        }
      };

      reader.onerror = (error) => {
        reject(error);
      };
    });
  };

  const html = (
    email: string,
    message: string,
    name: string,
    surname: string
  ) =>
    `
            <strong>${name} ${surname}</strong>
            <br>
            <strong>Mail</strong>: ${email}

            <hr>

            <p>${message}</p>
        `;

  const onSubmit = handleSubmit(
    async ({ name, surname, message, email, cv }) => {
      const file = (await convertBase64(cv[0])) as string;

      if (!REACT_APP_MAIL_KEY) {
        console.error("REACT_APP_MAIL_KEY is not provided.");
        return;
      }

      if (policy) {
        const mail = {
          subject: `Candidatura per ${title}`,
          htmlContent: html(email, message, name, surname),
          sender: { email: "form@unitiva.it" },
          to: [{ email: "jobs@unitiva.it" }],
          attachment: [{ name: cv[0].name, content: file }],
        };
        if (typeof mail === "undefined") {
          setMailHasError(true);
          return;
        }
        const options = {
          method: "POST",
          headers: {
            accept: "application/json",
            "content-type": "application/json",
            "api-key": REACT_APP_MAIL_KEY,
          },
          body: JSON.stringify(mail),
        };
        fetch(BREVO_ENDPOINT, options)
          .then((resp) => {
            if (!resp.ok) {
              setMailHasError(true);
              console.error("Error sending email");
            }
            return resp.json();
          })
          .then((resp) => {
            setMailSended(true);
            console.log("Email sent succesfully", resp);
          })
          .catch((err) => {
            setMailHasError(true);
            console.error("Failed to send email:", err);
          });

        setMailSended(true);
        reset();
      }
    }
  );

  const onChangeHandler: ChangeEventHandler<HTMLInputElement> = (e) => {
    const files = e.currentTarget.files;

    if (files) {
      setSelectedFile(files[0]);
    }
  };

  return (
    <>
      <S.Form onSubmit={onSubmit}>
        <div>
          <Image src={formImage} alt="form image" />
        </div>
        {mailSended ? (
          <S.MailSendedWrapper>
            <Title
              margin={[24, 0]}
              as="h1"
              title="Grazie per aver inviato la tua candidatura!"
              size="2.4"
              centered
            />
            <p>
              Il tuo cv verrà esaminato e se in linea con le nostre richieste
              sarai ricontattato a breve.
            </p>
          </S.MailSendedWrapper>
        ) : (
          <>
            {mailHasError ? (
              <S.MailSendedWrapper>
                <Title
                  margin={[24, 0]}
                  as="h1"
                  title="Attenzione!"
                  size="2.4"
                  centered
                />
                <p>
                  Non è stato possibile inviare la candidatura, ricarica la
                  pagina e riprova. Se il problema persiste manda la tua
                  candidatura a <strong>jobs@unitiva.it</strong> dal tuo
                  provider perferito.
                </p>
              </S.MailSendedWrapper>
            ) : (
              <>
                <Title
                  margin={[24, 0]}
                  as="h1"
                  title="Sottoponi la tua candidatura"
                  size="2.4"
                  centered
                />
                <S.InputWrapper>
                  <S.Input
                    placeholder="Nome"
                    type="text"
                    name="name"
                    ref={register({ required: true })}
                    $error={!!errors.name || false}
                  />
                  <S.Label>Nome</S.Label>
                  {errors.name && (
                    <span className="error">
                      &#42; Questo campo è obbligatorio
                    </span>
                  )}
                </S.InputWrapper>
                <S.InputWrapper>
                  <S.Input
                    placeholder="Cognome"
                    type="text"
                    name="surname"
                    ref={register({ required: true })}
                    $error={!!errors.surname || false}
                  />
                  <S.Label>Cognome</S.Label>
                  {errors.surname && (
                    <span className="error">
                      &#42; Questo campo è obbligatorio
                    </span>
                  )}
                </S.InputWrapper>
                <S.InputWrapper>
                  <S.Input
                    placeholder="Email"
                    type="email"
                    name="email"
                    ref={register({ required: true })}
                    $error={!!errors.email || false}
                  />
                  <S.Label>Email</S.Label>
                  {errors.email && (
                    <span className="error">
                      &#42; Questo campo è obbligatorio
                    </span>
                  )}
                </S.InputWrapper>
                <S.InputWrapper>
                  <S.Textarea
                    placeholder="Scrivi qui il tuo messaggio"
                    name="message"
                    ref={register({ required: true })}
                    $error={!!errors.message || false}
                  />
                  <S.Label>Scrivi qui il tuo messaggio</S.Label>
                  {errors.message && (
                    <span className="error">
                      &#42; Questo campo è obbligatorio
                    </span>
                  )}
                </S.InputWrapper>
                <S.InputWrapper>
                  <S.SendCVLabel htmlFor="send-cv">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="22"
                      height="23"
                      viewBox="0 0 22 23"
                    >
                      <path
                        data-name="Tracciato 5140"
                        d="M300.833-2862.917h18.333v-19.167H300.833Zm20.167.958v-21.083a.939.939,0,0,0-.917-.958H299.917a.939.939,0,0,0-.917.958v21.083a.938.938,0,0,0,.917.958h20.167A.938.938,0,0,0,321-2861.958Zm-5.769-10.822a.9.9,0,0,1-.648.281.9.9,0,0,1-.648-.281l-3.019-3.156v9.187a.938.938,0,0,1-.917.958.938.938,0,0,1-.917-.958v-9.187l-3.019,3.156a.888.888,0,0,1-1.3,0,.988.988,0,0,1,0-1.355l4.583-4.792a.889.889,0,0,1,1.3,0l4.583,4.792A.988.988,0,0,1,315.231-2872.781Z"
                        transform="translate(-299 2884)"
                        fill="#4260d1"
                        fillRule="evenodd"
                      />
                    </svg>
                    {selectedFile
                      ? selectedFile.name
                      : "Carica il tuo curriculum"}
                  </S.SendCVLabel>
                  <S.SendCV
                    type="file"
                    id="send-cv"
                    accept=".pdf"
                    name="cv"
                    ref={register({ required: true })}
                    onChange={onChangeHandler}
                  />
                  {errors.cv && (
                    <span className="error">
                      &#42; Questo campo è obbligatorio
                    </span>
                  )}
                </S.InputWrapper>
                <S.InputWrapper>
                  <S.AcceptPolicyWrapper>
                    <S.AcceptPolicy
                      type="checkbox"
                      name="policy"
                      onChange={() => setPolicy(!policy)}
                    />
                    <span>
                      Autorizzo il trattamento dei miei dati ai sensi del{" "}
                      <a href={informativaPolicy} target="_blank">
                      regolamento UE 679/2016 e
                        Codice Privacy D.lgs 196/03
                      </a>{" "}
                      e s.m.e.i.
                    </span>
                  </S.AcceptPolicyWrapper>
                  {!policy && (
                    <div className="error">
                      &#42; è richiesto il consenso alla nostra policy sulla
                      privacy
                    </div>
                  )}
                </S.InputWrapper>
                <Button
                  icon={
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="33.425"
                      height="48"
                      viewBox="0 0 33.425 48"
                    >
                      <path
                        d="M335-1295a3.162,3.162,0,0,0-3-3,3.009,3.009,0,0,0-3,3v-2a3.009,3.009,0,0,0-3-3,3.009,3.009,0,0,0-3,3v-2a3.009,3.009,0,0,0-3-3,3.009,3.009,0,0,0-3,3v-12a3.009,3.009,0,0,0-3-3,3.009,3.009,0,0,0-3,3v23.984a41.723,41.723,0,0,1-2.376-4.114c-1.718-3.764-3.806-3.292-5.208-2.422a3.01,3.01,0,0,0-.968,4.132,109.106,109.106,0,0,0,7.436,11.378c3.428,4.23,6.814,6.042,9.116,6.042h6c3.3,0,5.484-2.3,8-6,3.506-5.154,2.254-14.418,2-17m-12-16a8.983,8.983,0,0,1-4,7.48V-1311a5.015,5.015,0,0,0-5-5,5.015,5.015,0,0,0-5,5v7.48a8.983,8.983,0,0,1-4-7.48,9,9,0,0,1,9-9,9,9,0,0,1,9,9"
                        transform="translate(-302 1320)"
                        fill="#fff"
                        fillRule="evenodd"
                      />
                    </svg>
                  }
                  className="btn-continue"
                  label="Invia candidatura"
                />
              </>
            )}
          </>
        )}
      </S.Form>
    </>
  );
};

export default VacancyForm;
